import { getSessionAuthentication, rms2BasePath } from '../common/Api';

interface FetchOptions {
    method: string;
    headers: Record<string, string>;
    body?: string;
}

function getHeaders(): Record<string, string> {
    const auth = getSessionAuthentication();
    return {
        'Content-Type': 'application/json',
        'Authorization': auth.token,
        'x-cleverapps-tenant': auth.tenantStringIdentifier,
    };
}

async function rms2Fetch<T>(path: string, options?: Partial<FetchOptions>): Promise<T> {
    const response = await fetch(rms2BasePath + path, {
        method: options?.method ?? 'GET',
        headers: getHeaders(),
        body: options?.body,
    });
    if (!response.ok) {
        const text = await response.text();
        let message = `API error: ${response.status} ${response.statusText}`;
        try {
            const parsed = JSON.parse(text);
            if (parsed?.message) message = parsed.message;
        } catch { /* ignore */ }
        throw new Error(message);
    }
    const text = await response.text();
    return text ? JSON.parse(text) : null;
}

export interface QuantityGroup {
    id: number;
    name: string;
}

export interface VolumeDiscountLimit {
    minimumQuantity: number;
    discountPercent: number;
}

export interface VolumeDiscount {
    id: number;
    name: string;
    quantityGroup: QuantityGroup | null;
    limits: VolumeDiscountLimit[];
}

export async function getVolumeDiscount(id: number): Promise<VolumeDiscount> {
    return rms2Fetch<VolumeDiscount>(`volumediscounts/${id}`);
}

export async function createVolumeDiscount(vd: Partial<VolumeDiscount>): Promise<VolumeDiscount> {
    return rms2Fetch<VolumeDiscount>('volumediscounts', {
        method: 'POST',
        body: JSON.stringify({
            name: vd.name,
            quantityGroupId: vd.quantityGroup?.id,
            limits: vd.limits,
        }),
    });
}

export async function updateVolumeDiscount(vd: VolumeDiscount): Promise<VolumeDiscount> {
    return rms2Fetch<VolumeDiscount>(`volumediscounts/${vd.id}`, {
        method: 'PUT',
        body: JSON.stringify({
            name: vd.name,
            quantityGroupId: vd.quantityGroup?.id,
            limits: vd.limits,
        }),
    });
}

export async function getQuantityGroups(): Promise<QuantityGroup[]> {
    const response = await rms2Fetch<{ quantityGroups: QuantityGroup[] }>('volumediscounts/quantitygroups');
    return response?.quantityGroups ?? [];
}
